import { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Clock, ArrowRight, CircleCheck, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, FadeIn } from "@/components/motion";
import { API, usePageMeta } from "@/lib/site";

const TOPICS = ["General inquiry", "Partnership", "Church or group visit", "Media", "Golf Scramble 2026"];

const INFO = [
  { icon: MapPin, label: "Visit us", value: "2620 Apodaca Rd, Las Cruces, NM 88005", testid: "contact-info-address" },
  { icon: Mail, label: "Email", value: "sue@hopecenters.org", testid: "contact-info-email" },
  { icon: Clock, label: "Office hours", value: "Mon–Fri, 9am–5pm MT", testid: "contact-info-hours" },
];

const Contact = () => {
  usePageMeta(
    "Contact — Hope Centers International",
    "Get in touch with Hope Centers International: partner, plan a visit, or ask a question."
  );

  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const valid = form.name.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(form.email) && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-44 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <FadeIn y={16}>
          <p className="label">Contact</p>
        </FadeIn>
        <h1
          data-testid="contact-heading"
          className="mt-6 font-serif font-medium text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.98]"
        >
          <MaskedLines delay={0.15} lines={[<>Let's talk</>, <><em className="text-terra">hope.</em></>]} />
        </h1>
        <FadeIn delay={0.5} className="mt-8 max-w-xl">
          <p className="text-base sm:text-lg leading-relaxed text-ink/75">
            Questions, partnerships, or a visit to the centers — every message lands with a real person
            on our team.
          </p>
        </FadeIn>

        <div className="mt-16 md:mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <FadeIn className="lg:col-span-5">
            <div className="space-y-2">
              {INFO.map((item, i) => (
                <div
                  key={item.label}
                  data-testid={item.testid}
                  className={`flex items-start gap-4 py-6 ${i > 0 ? "border-t border-ink/10" : ""}`}
                >
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-terra/10 text-terra shrink-0">
                    <item.icon size={18} />
                  </span>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink-soft">{item.label}</p>
                    <p className="mt-1.5 font-semibold text-ink">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 border-t border-ink/10 pt-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-ink-soft">Follow us</p>
              <div className="mt-4 flex items-center gap-3">
                <a
                  href="https://www.facebook.com/HopeCentersIntl"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-social-facebook"
                  aria-label="Hope Centers on Facebook"
                  className="grid place-items-center w-11 h-11 rounded-full border border-ink/20 text-ink transition-[color,border-color,transform] duration-300 hover:text-terra hover:border-terra hover:scale-105"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href="https://www.instagram.com/hopecenters"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-social-instagram"
                  aria-label="Hope Centers on Instagram"
                  className="grid place-items-center w-11 h-11 rounded-full border border-ink/20 text-ink transition-[color,border-color,transform] duration-300 hover:text-terra hover:border-terra hover:scale-105"
                >
                  <Instagram size={18} />
                </a>
              </div>
            </div>
            <div className="mt-10 bg-forest text-paper rounded-2xl p-8">
              <p className="font-serif text-2xl md:text-3xl font-medium tracking-tight leading-snug">
                Want to see the work up close?
              </p>
              <p className="mt-4 text-sm text-paper/70 leading-relaxed">
                Ask us about visiting a center or bringing your church or group — choose a topic in the form and
                we'll take it from there.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div data-testid="contact-success" className="bg-white border border-ink/10 rounded-2xl p-10 md:p-14">
                <span className="grid place-items-center w-14 h-14 rounded-full bg-terra/10 text-terra">
                  <CircleCheck size={26} />
                </span>
                <h2 className="mt-8 font-serif text-4xl md:text-5xl font-medium tracking-tight">
                  Message received.
                </h2>
                <p className="mt-4 text-ink/70 leading-relaxed max-w-md">
                  Thank you, {form.name.split(" ")[0]}. Someone from our team will reply to {form.email} within two
                  business days.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-button"
                  onClick={() => {
                    setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
                    setSent(false);
                  }}
                  className="btn-outline mt-8"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white border border-ink/10 rounded-2xl p-8 md:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="contact-name" className="label block mb-1">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      className="field"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="label block mb-1">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.org"
                      className="field"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-topic" className="label block mb-1">
                    I'm reaching out about
                  </label>
                  <select
                    id="contact-topic"
                    data-testid="contact-topic-select"
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="field cursor-pointer"
                  >
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="label block mb-1">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="How can we help?"
                    className="field resize-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={!valid || sending}
                  className="btn-primary group disabled:opacity-40 disabled:pointer-events-none"
                >
                  {sending ? "Sending…" : "Send message"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default Contact;
